import React from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import { SkeletonText, SkeletonBlock } from "@ui-components/Skeleton/GenericSkeleton";
import { TenantDetail } from "../request/request";

// ====================== View Customer Modal ======================
export interface CustomerViewModalProps {
  isOpen: boolean;
  isLoading: boolean;
  customer: TenantDetail | null;
  onClose: () => void;
}

export const CustomerViewModal: React.FC<CustomerViewModalProps> = ({
  isOpen,
  isLoading,
  customer,
  onClose,
}) => {
  return (
    <GenericModal
      isOpen={isOpen}
      onClose={onClose}
      title="Customer Details"
      size="lg"
    >
      {isLoading ? (
        <div className="space-y-6">
          <SkeletonBlock h={20} w="35%" className="mb-3" />
          <div className="grid grid-cols-3 gap-4">
            {Array.from({ length: 12 }).map((_, idx) => (
              <SkeletonText key={idx} lines={2} lineHeight={14} />
            ))}
          </div>
        </div>
      ) : (
        customer && (
          <div className="grid grid-cols-3 gap-4">
            <GenericInput
              label="ID"
              value={String(customer.id ?? "")}
              readOnly
            />
            <GenericInput
              label="Name"
              value={customer.Name ?? ""}
              readOnly
            />
            <GenericInput
              label="Display Name"
              value={customer.nameDisplay ?? ""}
              readOnly
            />
            <GenericInput
              label="Domain"
              value={customer.Domain ?? ""}
              readOnly
            />
            <GenericInput
              label="Chargebee Customer ID"
              value={customer.chargebeeCustomerId ?? ""}
              readOnly
            />
            <GenericInput
              label="Org ID"
              value={customer.OrgID ?? ""}
              readOnly
            />
            <GenericInput
              label="Jira Organization ID"
              value={customer.jiraOrganizationId ?? ""}
              readOnly
            />
            <GenericInput
              label="Tenant Type"
              value={customer.TenantType?.name ?? ""}
              readOnly
            />
            <GenericInput
              label="Has MFA"
              value={customer.hasMFA ? "Yes" : "No"}
              readOnly
            />
            <GenericInput
              label="Can Access Alerts"
              value={customer.canAccessAlerts ? "Yes" : "No"}
              readOnly
            />
            <GenericInput
              label="Created At"
              value={customer.createdAt ? new Date(customer.createdAt).toLocaleString() : ""}
              readOnly
            />
            <GenericInput
              label="Updated At"
              value={customer.updatedAt ? new Date(customer.updatedAt).toLocaleString() : ""}
              readOnly
            />
          </div>
        )
      )}
    </GenericModal>
  );
};

// ====================== Edit Customer Modal ======================
export interface CustomerEditModalProps {
  isOpen: boolean;
  isLoading: boolean;
  customer: TenantDetail | null;
  onClose: () => void;
  onSave: () => void;
  onFieldChange: (field: string, value: string | boolean) => void;
}

export const CustomerEditModal: React.FC<CustomerEditModalProps> = ({
  isOpen,
  isLoading,
  customer,
  onClose,
  onSave,
  onFieldChange,
}) => {
  return (
    <GenericModal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Customer"
      size="lg"
    >
      {isLoading ? (
        <div className="space-y-6">
          <SkeletonBlock h={20} w="30%" className="mb-3" />
          <div className="grid grid-cols-3 gap-4">
            {Array.from({ length: 12 }).map((_, idx) => (
              <SkeletonText key={idx} lines={2} lineHeight={14} />
            ))}
          </div>
        </div>
      ) : customer ? (
        <div className="space-y-6">
          <div className="grid grid-cols-3 gap-4">
            <GenericInput
              label="ID"
              value={String(customer.id ?? "")}
              readOnly
            />
            <GenericInput
              label="Name"
              value={customer.Name ?? ""}
              onChange={(value) => onFieldChange("Name", value)}
            />
            <GenericInput
              label="Display Name"
              value={customer.nameDisplay ?? ""}
              onChange={(value) => onFieldChange("nameDisplay", value)}
            />
            <GenericInput
              label="Domain"
              value={customer.Domain ?? ""}
              onChange={(value) => onFieldChange("Domain", value)}
            />
            <GenericInput
              label="Chargebee Customer ID"
              value={customer.chargebeeCustomerId ?? ""}
              onChange={(value) => onFieldChange("chargebeeCustomerId", value)}
            />
            <GenericInput
              label="Org ID"
              value={customer.OrgID ?? ""}
              onChange={(value) => onFieldChange("OrgID", value)}
            />
            <GenericInput
              label="Jira Organization ID"
              value={customer.jiraOrganizationId ?? ""}
              onChange={(value) => onFieldChange("jiraOrganizationId", value)}
            />
            <GenericInput
              label="Tenant Type"
              value={customer.TenantType?.name ?? ""}
              readOnly
            />
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <button
              className="px-4 py-2 border border-semantic-border-secondary rounded text-semantic-text-secondary hover:bg-semantic-state-active"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 bg-semantic-bg-brand-primary text-white rounded hover:bg-semantic-bg-brand-primary-hover"
              onClick={onSave}
            >
              Save
            </button>
          </div>
        </div>
      ) : (
        <div className="text-center py-8 text-semantic-text-secondary">
          No customer data available
        </div>
      )}
    </GenericModal>
  );
};

